import React from 'react'
import { Formik, Form, FormikProps } from 'formik'
import { Button, Textarea } from '@headlessui/react'
import { X } from 'lucide-react'
import { LoaderButton } from 'components/common/LoadingButton'
import { Note, NoteFormValues } from 'types/crm/notes'
import { noteFormInitialValues, noteFormValidationSchema } from 'utils/validation'
import OwnerSearchDropdown from '../Dropdowns/OwnerSearchDropdown'
import FormTextarea from 'components/common/FormTextArea'


type Owners = React.ComponentProps<typeof OwnerSearchDropdown>['owners']

/** Maps an existing note onto form shape, or falls back to blank defaults for create. */
const getInitialValues = (note?: Note | null): NoteFormValues => {
    if (!note) return noteFormInitialValues

    return {
        ...noteFormInitialValues,
        note: note.note ?? '',
        associatedPersonUuid: note.associatedPerson?.uuid ?? null,
    }
}

export interface CreateNoteProps {
    /** UUID of the record this note belongs to (contact, deal, company, ...) */
    entityUuid: string
    owners: Owners
    onCancel: () => void
    onSubmit: (
        values: NoteFormValues,
        helpers: { resetForm: () => void },
    ) => void | Promise<void>
    /** Optional ref if the parent needs to imperatively reset the form (e.g. on tab switch) */
    formikRef?: React.Ref<FormikProps<NoteFormValues>>
    isSubmitting?: boolean
    title?: string
    placeholder?: string
    /** Pass the note being edited to pre-fill the form; omit/null for create mode. */
    note?: Note | null
}

const CreateNote = ({
    entityUuid,
    owners,
    onCancel,
    onSubmit,
    formikRef,
    isSubmitting = false,
    title,
    placeholder = 'Write a note about this record',
    note = null,
}: CreateNoteProps) => {
    const isEditing = !!note
    const heading = title ?? (isEditing ? 'Edit Note' : 'Note')

    return (
        <Formik<NoteFormValues>
            innerRef={formikRef}
            initialValues={getInitialValues(note)}
            enableReinitialize
            validationSchema={noteFormValidationSchema}
            onSubmit={onSubmit}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
                resetForm,
            }: FormikProps<NoteFormValues>) => {
                const handleCancel = () => {
                    resetForm()
                    onCancel()
                }

                return (
                    <Form onSubmit={handleSubmit}>
                        <div className="rounded-xl bg-primary-light border border-primary-subtle p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-neutral-faded font-medium">{heading}</h3>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center justify-center gap-2">
                                        <p className="text-sm text-neutral-faded whitespace-nowrap">Assigned to:</p>
                                        <OwnerSearchDropdown
                                            owners={owners}
                                            currentOwnerUuid={values.associatedPersonUuid}
                                            onOwnerChange={(owner) =>
                                                setFieldValue('associatedPersonUuid', owner?.uuid ?? null)
                                            }
                                            placeholder="Select owner"
                                            allowUnassigned={true}
                                            unassignedLabel="None"
                                            size="md"
                                            className="rounded-full border border-primary-subtle "
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="text-neutral-faded hover:text-neutral"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* <Textarea
                                name="note"
                                value={values.note}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder={placeholder}
                                rows={4}
                                className={`w-full rounded-lg bg-inherit border p-3 text-sm text-neutral placeholder:font-normal placeholder:text-base placeholder:text-[#374151] focus:outline-none resize-none transition-colors ${touched.note && errors.note
                                    ? 'border-red-500'
                                    : 'border-primary-subtle focus:border-emphasis'
                                    }`}
                            />
                            {touched.note && errors.note && (
                                <p className="text-xs text-red-500 mt-1">{errors.note}</p>
                            )} */}
                            <FormTextarea
                                name="note"
                                // label="Notes"
                                placeholder="Write note.."
                                maxLength={500}
                                // darkBg
                                className={{ label: 'text-sm', textarea: 'text-sm' }}
                            />

                            <div className="flex items-center justify-end gap-3 mt-4">
                                <Button
                                    type="button"
                                    onClick={handleCancel}
                                    className="py-2 px-4 rounded-lg border border-primary-subtle text-neutral text-sm font-medium hover:bg-primary-surface transition-colors"
                                >
                                    Cancel
                                </Button>
                                <LoaderButton
                                    type="submit"
                                    isLoading={isSubmitting}
                                    text={isEditing ? 'Save changes' : 'Create note'}
                                    className="!mt-0"
                                />
                            </div>
                        </div>
                    </Form>
                )
            }}
        </Formik>
    )
}

export default CreateNote